import { createAsyncThunk } from '@reduxjs/toolkit';
import { createGenericSlice, GenericState } from '../GenericSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { SECRETS_ENV } from '../../Config/firebase';
import { db } from '../../Config/firebase';
import { doc, deleteDoc, getDoc } from 'firebase/firestore';
import axios from 'axios';

// Define the Zoom state interface
export interface ZoomData {
    isAuthenticated: boolean;
    userEmail: string | null;
    name?: string | null;
    personalMeetingUrl?: string | null;
    accessToken?: string | null;
}

// Local storage key for Zoom state persistence
const ZOOM_STORAGE_KEY = 'zoom_auth_state';

// Helper function to load persisted state
const loadPersistedState = (): ZoomData => {
    try {
        const persisted = localStorage.getItem(ZOOM_STORAGE_KEY);
        if (persisted) {
            const parsed = JSON.parse(persisted);
            // Only use persisted state if it's not too old (24 hours)
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < maxAge) {
                console.log('📦 Loading persisted Zoom state:', parsed.data);
                console.log('📅 Persisted state age:', Math.round((Date.now() - parsed.timestamp) / (1000 * 60)), 'minutes');
                return parsed.data;
            } else {
                console.log('🗑️ Persisted Zoom state is too old, clearing');
                console.log('📅 Persisted state age:', Math.round((Date.now() - parsed.timestamp) / (1000 * 60)), 'minutes (max: 1440)');
                localStorage.removeItem(ZOOM_STORAGE_KEY);
            }
        } else {
            console.log('📭 No persisted Zoom state found');
        }
    } catch (error) {
        console.error('Error loading persisted Zoom state:', error);
        localStorage.removeItem(ZOOM_STORAGE_KEY);
    }
    return {
        isAuthenticated: false,
        userEmail: null,
        name: null,
        personalMeetingUrl: null,
        accessToken: null,
    };
};

// Helper function to save state to localStorage
const savePersistedState = (state: ZoomData) => {
    try {
        const dataToPersist = {
            data: state,
            timestamp: Date.now()
        };
        localStorage.setItem(ZOOM_STORAGE_KEY, JSON.stringify(dataToPersist));
        console.log('💾 Persisting Zoom state:', state);
    } catch (error) {
        console.error('Error saving Zoom state to localStorage:', error);
    }
};

// Helper function to clear persisted state
export const clearPersistedZoomState = () => {
    try {
        localStorage.removeItem(ZOOM_STORAGE_KEY);
        console.log('🗑️ Cleared persisted Zoom state');
    } catch (error) {
        console.error('Error clearing Zoom state from localStorage:', error);
    }
};

// Helper function to check if user has been away for a long time
const isLongAbsence = (lastCheckTime: number): boolean => {
    const longAbsenceThreshold = 6 * 60 * 60 * 1000; // 6 hours
    return (Date.now() - lastCheckTime) > longAbsenceThreshold;
};

// Utility function to check if authentication check is needed
export const shouldCheckZoomAuth = (): boolean => {
    try {
        const persisted = localStorage.getItem(ZOOM_STORAGE_KEY);
        if (!persisted) {
            return true; // No persisted state, need to check
        }

        const parsed = JSON.parse(persisted);
        if (!parsed.timestamp) {
            return true; // No timestamp, need to check
        }

        // Check if it's been more than 1 hour since last check
        const oneHour = 60 * 60 * 1000;
        if ((Date.now() - parsed.timestamp) > oneHour) {
            return true; // Been more than an hour, need to check
        }

        // If we have valid persisted state and it's recent, no need to check
        return false;
    } catch (error) {
        console.error('Error checking if Zoom auth is needed:', error);
        return true; // On error, check anyway
    }
};

// Utility function to get current persisted state for debugging
export const getCurrentPersistedZoomState = () => {
    try {
        const persisted = localStorage.getItem(ZOOM_STORAGE_KEY);
        if (persisted) {
            const parsed = JSON.parse(persisted);
            return {
                data: parsed.data,
                timestamp: parsed.timestamp,
                age: parsed.timestamp ? Math.round((Date.now() - parsed.timestamp) / (1000 * 60)) : null
            };
        }
        return null;
    } catch (error) {
        console.error('Error getting persisted Zoom state:', error);
        return null;
    }
};

const initialState: GenericState<ZoomData> = {
    data: loadPersistedState(),
    status: 'not_loaded',
    error: null,
};

const API_ENDPOINT = SECRETS_ENV.API_END_POINT;

// Check Zoom authentication status
export const checkZoomAuth = createAsyncThunk(
    'zoom/checkAuth',
    async (serviceProviderId: string) => {
        try {
            console.log('🔍 Starting Zoom auth check for:', serviceProviderId);
            
            // Check if this is a long absence and we should force a fresh check
            const persisted = localStorage.getItem(ZOOM_STORAGE_KEY);
            if (persisted) {
                const parsed = JSON.parse(persisted);
                if (parsed.timestamp && isLongAbsence(parsed.timestamp)) {
                    console.log('⏰ Long absence detected, forcing fresh authentication check');
                }
            }
            
            // First check Firestore for existing tokens
            const zoomDoc = await getDoc(doc(db, 'ServiceProviders', serviceProviderId, 'MeetingProviders', 'zoom'));
            
            if (!zoomDoc.exists()) {
                console.log('❌ No Zoom document found for:', serviceProviderId);
                const result = {
                    isAuthenticated: false,
                    userEmail: null,
                    name: null,
                    personalMeetingUrl: null,
                    accessToken: null
                };
                savePersistedState(result);
                return result;
            }

            const data = zoomDoc.data();
            console.log('✅ Found Zoom document:', { 
                providerEmail: data.providerEmail, 
                expiresAt: data.expiresAt,
                currentTime: Date.now()
            });
            
            // Check if token is expired
            const isExpired = Date.now() >= data.expiresAt;
            
            if (isExpired) {
                console.log('⚠️ Token expired, attempting refresh...');
                // Try to refresh the token
                try {
                    const response = await axios.post(`${API_ENDPOINT}/api/zoom/refresh-token`, {
                        userId: serviceProviderId
                    });
                    
                    if (response.data.success) {
                        console.log('✅ Token refreshed successfully');
                        // Get fresh data after refresh
                        const refreshedDoc = await getDoc(doc(db, 'ServiceProviders', serviceProviderId, 'MeetingProviders', 'zoom'));
                        const refreshedData = refreshedDoc.data();
                        
                        if (refreshedData) {
                            const result = {
                                isAuthenticated: true,
                                userEmail: refreshedData.providerEmail,
                                name: `${refreshedData.firstName} ${refreshedData.lastName}`,
                                personalMeetingUrl: refreshedData.personalMeetingUrl,
                                accessToken: refreshedData.accessToken
                            };
                            savePersistedState(result);
                            return result;
                        }
                    }
                } catch (refreshError) {
                    console.error('❌ Error refreshing Zoom token:', refreshError);
                    const result = {
                        isAuthenticated: false,
                        userEmail: null,
                        name: null,
                        personalMeetingUrl: null,
                        accessToken: null
                    };
                    savePersistedState(result);
                    return result;
                }
            }

            // Token is valid, return user info immediately
            console.log('✅ Token is valid, returning user info');
            const result = {
                isAuthenticated: true,
                userEmail: data.providerEmail,
                name: `${data.firstName} ${data.lastName}`,
                personalMeetingUrl: data.personalMeetingUrl,
                accessToken: data.accessToken
            };
            savePersistedState(result);
            return result;
        } catch (error) {
            console.error('❌ Error checking Zoom auth:', error);
            const result = {
                isAuthenticated: false,
                userEmail: null,
                name: null,
                personalMeetingUrl: null,
                accessToken: null
            };
            savePersistedState(result);
            return result;
        }
    }
);

// Revoke Zoom access
export const revokeZoomAccess = createAsyncThunk(
    'zoom/revokeAccess',
    async (serviceProviderId: string) => {
        try {
            // Delete the Zoom document from MeetingProviders collection
            await deleteDoc(doc(db, 'ServiceProviders', serviceProviderId, 'MeetingProviders', 'zoom'));
            
            const result = {
                isAuthenticated: false,
                userEmail: null,
                name: null,
                personalMeetingUrl: null,
                accessToken: null
            };
            savePersistedState(result);
            return result;
        } catch (error) {
            console.error('Error removing Zoom document:', error);
            throw error;
        }
    }
);

// Selector for Zoom access token
export const selectZoomAccessToken = (state: { zoomSlice: GenericState<ZoomData> }) => 
    state.zoomSlice.data?.accessToken || null;

const zoomSlice = createGenericSlice({
    name: 'zoom',
    initialState,
    reducers: {
        reset: (state) => {
            state.data = {
                isAuthenticated: false,
                userEmail: null,
                name: null,
                personalMeetingUrl: null,
                accessToken: null,
            };
            state.status = 'not_loaded';
            state.error = null;
            localStorage.removeItem(ZOOM_STORAGE_KEY);
        },
        // Add a reducer to update state and persist it
        updateZoomState: (state, action: PayloadAction<Partial<ZoomData>>) => {
            if (!state.data) return;
            
            const updatedData: ZoomData = {
                isAuthenticated: action.payload.isAuthenticated ?? state.data.isAuthenticated,
                userEmail: action.payload.userEmail ?? state.data.userEmail,
                name: action.payload.name ?? state.data.name,
                personalMeetingUrl: action.payload.personalMeetingUrl ?? state.data.personalMeetingUrl,
                accessToken: action.payload.accessToken ?? state.data.accessToken,
            };
            state.data = updatedData;
            savePersistedState(updatedData);
        },
    },
    extraReducers: (builder: any) => {
        builder
            // Check Auth
            .addCase(checkZoomAuth.pending, (state: GenericState<ZoomData>) => {
                state.status = 'loading';
            })
            .addCase(checkZoomAuth.fulfilled, (state: GenericState<ZoomData>, action: PayloadAction<ZoomData>) => {
                state.status = 'finished';
                state.data = action.payload;
                state.error = null;
                savePersistedState(action.payload);
            })
            .addCase(checkZoomAuth.rejected, (state: GenericState<ZoomData>, action: { error: { message?: string } }) => {
                state.status = 'error';
                state.error = action.error?.message || 'Failed to check Zoom authentication';
                // Don't clear persisted state on error, just update status
            })
            
            // Revoke Access
            .addCase(revokeZoomAccess.pending, (state: GenericState<ZoomData>) => {
                state.status = 'loading';
            })
            .addCase(revokeZoomAccess.fulfilled, (state: GenericState<ZoomData>, action: PayloadAction<ZoomData>) => {
                state.status = 'finished';
                state.data = action.payload;
                state.error = null;
                savePersistedState(action.payload);
            })
            .addCase(revokeZoomAccess.rejected, (state: GenericState<ZoomData>, action: { error: { message?: string } }) => {
                state.status = 'error';
                state.error = action.error?.message || 'Failed to revoke Zoom access';
            });
    },
});

export const { reset: resetZoomState, updateZoomState } = zoomSlice.actions;

export default zoomSlice.reducer; 